/**
 * "Deployed but not billed" reconciliation — client.
 *
 * Read-only. Internal staff + superadmin only; the API is the enforcement point
 * and the `unbilled_deployments_view` capability only gates the sidebar entry.
 */
import { adminFetch, getAuthHeaders, API_BASE_URL } from './adminApi';

/** Why a row is on the report. Mirrors UNBILLED_FLAGS on the server. */
export type UnbilledFlag =
  | 'no_rental_link'
  | 'status_rented_without_rental'
  | 'status_available_while_deployed'
  | 'group_company_mismatch'
  | 'scheduled_window_elapsed'
  | 'hold_starts_later';

export interface UnbilledDeployment {
  deployment_id: string;
  unit_id: string;
  unit_code: string;
  company_id: string;
  company_name: string | null;
  group_id: string | null;
  production_name: string | null;
  site_name: string | null;
  deployed_since: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  deployed_by_id: string | null;
  deployed_by_name: string | null;
  deployment_reason: string | null;
  current_status: string | null;
  flags: UnbilledFlag[];
  why_flagged: string;
}

export interface UnbilledDeploymentsResponse {
  generated_at: string;
  count: number;
  items: UnbilledDeployment[];
}

/** Short badge text per flag. `no_rental_link` is implicit on every row. */
export const UNBILLED_FLAG_BADGES: Record<UnbilledFlag, string> = {
  no_rental_link: 'No rental',
  status_rented_without_rental: 'Status: rented, no rental',
  status_available_while_deployed: 'Status: available',
  group_company_mismatch: 'Group/company mismatch',
  scheduled_window_elapsed: 'Schedule elapsed',
  hold_starts_later: 'Hold starts later',
};

/**
 * NOTE: adminFetch already prefixes `${API_BASE_URL}/v1/admin`, so this endpoint
 * is relative to that. Passing '/v1/admin/...' here produces
 * /api/v1/admin/v1/admin/... and 404s — see the URL test in __tests__.
 */
export async function getUnbilledDeployments(): Promise<UnbilledDeploymentsResponse> {
  return adminFetch('/unbilled-deployments');
}

/**
 * CSV export. adminFetch JSON-parses, so this uses the raw blob-download path.
 */
export async function downloadUnbilledDeploymentsCsv(): Promise<void> {
  const url = `${API_BASE_URL}/v1/admin/unbilled-deployments?format=csv`;
  const response = await fetch(url, { headers: { ...(await getAuthHeaders() as Record<string, string>) } });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(err.message || `CSV export failed: ${response.status}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = 'unbilled-deployments.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on a timer — immediate revoke can cancel the download in Safari.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}
